import _ from 'lodash';
import { useMsal, } from '@azure/msal-react';
import { useParams } from "react-router-dom";
import { useEffect, useState, createContext, useMemo } from "react";
import { Link } from "react-router-dom";
import LoadingAnimation from "../../components/loading/loading";
import { CompanyAPI } from '../../api/company/company-api-factory';
import PropertyAPI from '../../api/property/property-api-factory';
import { LandlordPropertiesTab } from '../../components/company/landlord-properties-tab';
import { LandlordMapTab } from '../../components/company/landlord-map';
import { LandlordNotesTab } from '../../components/company/landlord-notes-tab';
import { LandlordDetailsTab } from '../../components/company/landlord-details-tab';
import { LandlordContactsTab } from '../../components/company/landlord-contacts-tab';
import { LandlordDocumentsTab } from '../../components/company/landlord-documents-tab';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

export const LandlordContext = createContext();

export const LandlordDetail = () => {

    const { instance, inProgress, accounts } = useMsal();
    const { id, tab } = useParams();

    const companyApi = useMemo(() => new CompanyAPI(instance, inProgress, accounts, true), [instance, inProgress, accounts]); // TODO: REVERT - testing localStorage

    const getTabIndexFromName = tabName => {

        switch (tabName) {
            default:
            case "details":
                return 0;
            case "properties":
                return 1;
            case "map":
                return 2;
            case "notes":
                return 3;
            case "documents":
                return 4;
            case "contacts":
                return 5;
        }
    };

    const [landlordData, setLandlordData] = useState(null);
    const [fetchCompanyInProgress, setFetchCompanyInProgress] = useState(false);
    const [shouldFetchCompany, setShouldFetchCompany] = useState(true);
    const [saving, setSaving] = useState(false);
    const [rerender, setRerender] = useState(false);

    const [properties, setProperties] = useState(null);
    const [propertySearchInProgress, setPropertySearchInProgress] = useState(false);
    const [shouldFetchProperties, setShouldFetchProperties] = useState(true);
    const [tabIndex, setTabIndex] = useState(getTabIndexFromName(tab));

    useEffect(() => {
        if (shouldFetchCompany && !fetchCompanyInProgress) {


            setFetchCompanyInProgress(true);

            companyApi.getLandlord(id)
                .then(data => {
                    setLandlordData(data);
                })
                .catch(error => {
                    console.log("An error occurred getting data from the Company Data API.", error);
                    setLandlordData(null);
                })
                .finally(() => {
                    setShouldFetchCompany(false);
                    setFetchCompanyInProgress(false);
                });
        }
    }, [shouldFetchCompany, fetchCompanyInProgress, id, instance, inProgress, accounts]);

    const search = () => {
        if (!propertySearchInProgress && !properties && landlordData && shouldFetchProperties) {
            const api = new PropertyAPI(instance, inProgress, accounts, true);
            setPropertySearchInProgress(true);
            api.find(landlordData.name)
                .then((data) => {
                    setProperties(data);
                })
                .catch(error => console.log("An error occurred searching for properties:", error))
                .finally(() => {
                    setPropertySearchInProgress(false);
                    setShouldFetchProperties(false);
                });
        }
    };

    useEffect(() => {
        search();
    }, [propertySearchInProgress, landlordData, properties]);

    const saveLandlord = landlordData => {

        return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

            if (!saving) {
                setSaving(true);
                companyApi
                    .updateLandlord(landlordData.parent.id, landlordData)
                    .then(result => {
                        if (result.error) {
                            if (result.originalCompany.id === landlordData.parent.id) {
                                const existingLandlord = _.find(result.originalCompany.landlordsOrOwners, l => l.id === landlordData.id);
                                const newExistingLandlord = structuredClone(existingLandlord);
                                setLandlordData(newExistingLandlord);
                                setRerender(!rerender);
                                return resolve({ saveResult: { success: false, error: result.error }, landlord: newExistingLandlord });
                            }
                        }
                        else {
                            const newLandlord = _.find(result.landlordsOrOwners, l => l.id === landlordData.id);
                            setLandlordData(newLandlord);
                            setRerender(!rerender);
                            return resolve({ saveResult: { success: true, error: null }, landlord: newLandlord });
                        }
                    })
                    .catch(error => { console.error('An error occurred saving landlord --> company', error); reject(error) })
                    .finally(() => { console.log("Operation Completed"); setSaving(false) });
            }
        });
    };

    const saveContacts = contacts => {
        const newLandlord = structuredClone(landlordData);
        newLandlord.contacts = contacts;
        return saveLandlord(newLandlord);
    };

    const saveDocs = newDocs => {
        const newLandlord = structuredClone(landlordData);
        newLandlord.documents = _.filter(newDocs, d => d && !d.shouldDelete);
        return saveLandlord(newLandlord);
    };

    const context = {
        saveDocs: saveDocs
    };

    return (
        <LandlordContext.Provider value={context}>
            <>
                <ol className="breadcrumb float-xl-end">
                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                    <li className="breadcrumb-item"><Link to="/company/search">Company Search</Link></li>
                    <li className="breadcrumb-item active">Details</li>
                </ol>

                {!fetchCompanyInProgress && landlordData &&
                    <>
                        <h1 className="page-header">Details | <small>Manage customer details for <strong>{landlordData.displayName}</strong>, for <strong>{landlordData.parent.name}</strong></small></h1>
                        <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
                            <TabList className="nav nav-pills mb-2" >
                                <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 0 ? 'active' : ''} `}>Details</div></Tab>
                                <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 1 ? 'active' : ''} `}>Properties</div></Tab>
                                <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 2 ? 'active' : ''} `}>Map</div></Tab>
                                <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 3 ? 'active' : ''} `}>Notes</div></Tab>
                                <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 4 ? 'active' : ''} `}>Documents</div></Tab>
                                <Tab className="nav-item me-2"><div className={`nav-link ${tabIndex === 5 ? 'active' : ''} `}>Contacts</div></Tab>
                            </TabList>

                            <TabPanel>
                                <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                    <LandlordDetailsTab landlord={landlordData} saveLandlord={saveLandlord} />
                                </div>
                            </TabPanel>

                            <TabPanel>
                                <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                    <LandlordPropertiesTab landlord={landlordData} properties={properties} />
                                </div>
                            </TabPanel>

                            <TabPanel>
                                <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                    <LandlordMapTab propertyDocs={properties} landlord={landlordData} />
                                </div>
                            </TabPanel>

                            <TabPanel>
                                <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                    <LandlordNotesTab />
                                </div>
                            </TabPanel>

                            <TabPanel>
                                <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                    <LandlordDocumentsTab landlord={landlordData} />
                                </div>
                            </TabPanel>

                            <TabPanel>
                                <div className="tab-content p-3 rounded-top panel rounded-0 m-0">
                                    <LandlordContactsTab
                                        contacts={landlordData && landlordData.contacts ? landlordData.contacts : []}
                                        saveCallBack={saveContacts}
                                        saving={saving}
                                    />
                                </div>
                            </TabPanel>
                        </Tabs>
                    </>
                }
                {fetchCompanyInProgress && <LoadingAnimation />}
            </>
        </LandlordContext.Provider>
    )
}